/**
 * Which side of a pair fact a given entity is on — **the one implementation**,
 * imported by the server (`name-collision.service.ts`) and the dashboard
 * (`fact-copy.ts`) alike.
 *
 * It lives in the contract package rather than either plane because both planes
 * answer the same question about the same payload, and the two answers must not
 * be allowed to drift: the server writes "Parent of X" on the collision card and
 * the dashboard writes "Parent of X" on the brief, off the same `fields`. When
 * that logic was duplicated, one copy compared **names** and printed the
 * subject's own side as the counterparty on 27 prod lines.
 *
 * A pair fact reads "`a` is the {relation} of `b`" (server-design.md §7.2c), so
 * direction lives in `fields` — never in the columns.
 */

/** A resolved entity reference's id — absent on a ref the pre-pass never resolved. */
export const refEntityId = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null;
  const id = (value as { entity_id?: unknown }).entity_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

/**
 * The one case fold for an entity id — **every** comparison and every map key
 * built out of one goes through here.
 *
 * The two spellings are not equally free. An id read out of a **column** is
 * whatever Postgres renders a `uuid` as, which is lower case, always. An id read
 * out of a **`fields` ref** is whatever the payload was written with, which is
 * whatever the extractor's model happened to emit — and nothing downstream ever
 * normalizes it, because `withLiveRefIds` (`fact-read.service.ts`) only rewrites
 * the refs that actually *move* on a redirect. So an upper-cased stamp on an
 * already-live ref reaches a reader exactly as written, and a raw `===` against
 * a column id fails on a pair that is in fact the same entity.
 *
 * Folding is safe because a uuid's hex is case-insensitive by definition.
 */
const foldEntityId = (id: string | null | undefined): string | null =>
  typeof id === 'string' && id.length > 0 ? id.toLowerCase() : null;

/**
 * Do these two entity ids name the same entity?
 *
 * Case-insensitive, and `null`/empty is never equal to anything — including
 * another `null`, because "neither side is known" is not a match. This is the
 * comparison `pairSide` makes, and the one every caller that puts a `fields` ref
 * id next to a column id should make.
 */
export const sameEntityId = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  const left = foldEntityId(a);
  return left !== null && left === foldEntityId(b);
};

/**
 * Ref ids ride in jsonb, not in a typed column, so a payload can carry anything.
 * Only a uuid-shaped string is ever asked about — `id = ANY($1::uuid[])` errors
 * on the rest, and a whole brief read must not turn on one malformed stamp.
 *
 * The key is `sameEntityId`'s fold plus that shape gate, so an id looked up here
 * and an id compared there agree by construction: a map built on these keys is
 * keyed the way Postgres renders a uuid, and a payload's own spelling is under
 * no such obligation.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A payload ref's entity id as a lookup key: uuid-shaped and folded, or nothing. */
export const refEntityIdKey = (value: unknown): string | null => {
  const id = foldEntityId(refEntityId(value));
  return id !== null && UUID_SHAPE.test(id) ? id : null;
};

/**
 * One pair fact, as much of it as the side question needs: the payload, the
 * **other** column's entity id, and whether the entity being asked about sits in
 * the fact's subject column.
 */
export interface PairFactColumns {
  readonly fields: Record<string, unknown> | null | undefined;
  /** The entity id in the column the asked-about entity is *not* in, if known. */
  readonly otherId: string | null;
  /** Whether the asked-about entity is in the fact's `subject_entity_id` column. */
  readonly isSubjectSide: boolean;
}

/**
 * Which side of the pair the entity being described is on.
 *
 * `subjecting.ts` writes a pair fact with `fields.a` in the subject slot, so the
 * column looks like it is enough. It is not, and a merge is why: `entity-merge`
 * swaps the two columns to restore `subject = min(pair uuids)` after a repoint
 * and **deliberately leaves `fields` alone** (direction lives in `fields`, and
 * every other reader treats the two columns symmetrically). Taking direction
 * from the column would print "Parent of X" under a fact that says child.
 *
 * So the ids in `fields` are asked first, and the *other* side second: a merge
 * repoints the columns, not the refs, so a survivor's own id may be absent from
 * a fields payload whose far side still names the person being described. The
 * column is the fallback, for a row written before ids rode in `fields` at all.
 *
 * It used to be the fallback for a second case as well — **both** refs stamped
 * with merged-away twins, where no id probe can land — and there it was a guess
 * about write-time alignment that a merge's `subject = min(pair uuids)`
 * restoration had been free to swap. That case is now closed **before** this
 * function sees it: the server walks every ref id through its redirect chain
 * when it projects the fact (`liveRefIds` in `fact-read.service.ts`), so a
 * served payload carries live ids and the probe lands. This stays id-first
 * either way — the resolution is the server's job precisely because a redirect
 * is a thing only the substrate can see.
 *
 * Names are never consulted. An extracted spelling ("Shawn Johnson East") and an
 * entity's display name ("Shawn Johnson") are routinely different strings for
 * the same person, and that comparison is the bug this function replaced.
 *
 * Every id probe folds case (`sameEntityId`). The two spellings arrive from
 * different places and only one of them is normalized — a column id is however
 * Postgres renders a uuid, a ref id is however the payload was written — so a
 * raw `===` mis-sided an already-live upper-cased stamp and flipped the
 * direction word on the read.
 */
export function pairSide(row: PairFactColumns, entityId: string | null): 'a' | 'b' {
  const a = refEntityId(row.fields?.['a']);
  const b = refEntityId(row.fields?.['b']);
  if (entityId) {
    if (sameEntityId(a, entityId)) return 'a';
    if (sameEntityId(b, entityId)) return 'b';
  }
  if (sameEntityId(b, row.otherId)) return 'a';
  if (sameEntityId(a, row.otherId)) return 'b';
  return row.isSubjectSide ? 'a' : 'b';
}
